import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
} from '@wegroup/design-system';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Inputs } from '../../../../types/Enums';
import { NaceCode, NaceCodeList } from '../../../../types/CompanyScanTypes';
import { getFlowsDataTestId } from '../../../../utils/testUtils';
import NaceCodeSearchInput from './NaceCodeSearchInput';
import NaceCodeTags from './NaceCodeTags';
import NaceCodeTree from './NaceCodeTree';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  value: NaceCode[];
  onChange: (naceCodes: NaceCode[]) => void;
}

export interface FormValues {
  searchQuery: string;
}

const NaceCodeModal: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  onClose,
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const [naceCodes, setNaceCodes] = useState<NaceCode[]>(value);

  const { register, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      searchQuery: '',
    },
  });

  const handleAdd = (nace: NaceCodeList) => {
    setNaceCodes([...naceCodes, nace]);
  };

  const handleOnRemove = (naceCode: NaceCodeList) => {
    setNaceCodes((naceCodes) =>
      naceCodes.filter(({ code }) => code !== naceCode.code),
    );
  };

  const handleOnApply = () => {
    onChange(
      naceCodes.map(({ code, label, level, parent_code, section_code }) => ({
        code,
        label,
        level,
        parent_code,
        section_code,
      })),
    );
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('FLOWS_COMPANY-SCAN_NACE-INPUT_MODAL_TITLE')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody as={VStack} spacing="6" align="stretch">
          <NaceCodeSearchInput register={register} setValue={setValue} />
          {naceCodes.length > 0 && (
            <NaceCodeTags
              naceCodes={naceCodes}
              setNaceCodes={setNaceCodes}
              onRemove={handleOnRemove}
            />
          )}

          <NaceCodeTree
            naceCodes={naceCodes}
            watch={watch}
            onAdd={handleAdd}
            onRemove={handleOnRemove}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('CANCEL')}
          </Button>
          <Button
            onClick={handleOnApply}
            data-test-id={getFlowsDataTestId(Inputs.NACE_CODE, 'APPLY-BUTTON')}
          >
            {' '}
            {t('APPLY')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NaceCodeModal;
